import {
  Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import {
  distinctUntilChanged, filter, map, take, takeUntil,
} from 'rxjs/operators';
import { ProjectsService } from '../../../../../core/projects.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import { LocationPickerLauncherService } from '../../../../../../../shared/services/location-picker-launcher.service';
import { mergePickedLocation, recordLinkPayload } from '../../../../../../../shared/helpers/design-context';
import { SheetMarkupQueueService } from '../../../../../../../shared/services/sheet-markup-queue.service';
import { AuthService } from '../../../../../../../shared/services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { confirmLinkRemoval } from '../../../../../../../shared/helpers/link-removal';
import { MapFeatureLink } from '../../../../../../../shared/models/map-feature-link';
import { canModifyPunchlistItem } from './punchlist-item-permissions';
import { UploadFileRequest } from '../../../../../../../shared/core/shared.actions';
import { FetchAllBidItemsRequest } from '../../../../../core/projects.actions';
import { allBidItemsFilter, projectDetails } from '../../../../../core/projects.selectors';
import { projectForRoute } from '../../../../../../../shared/helpers/project-widget-config';
import {
  punchlistItemFieldMap,
  PunchlistItemField,
  PunchlistItemFieldMap,
  resolvePunchlistItemFields,
  withoutHiddenPunchlistItemFields,
} from '../../../../../../../shared/helpers/project-punchlist-item-config';
import {
  fromPunchlistFormValue,
  punchlistItemValues,
  PunchlistMultiValueField,
  PunchlistValueOption,
  punchlistValueOptions,
  toPunchlistFormValue,
} from '../../../../../../../shared/helpers/punchlist-item-values';
import { issueStatusText } from '../../../../../../../shared/helpers/issue-workflow';

/** The form's pick-list fields and the lookup each takes its values from. */
type PickListField = 'category' | 'type' | 'severity';
const PICK_LIST_LOOKUPS: Record<PickListField, string> = {
  category: 'punchlist_category',
  type: 'punchlist_type',
  severity: 'punchlist_severity',
};
const PICK_LIST_FIELDS = Object.keys(PICK_LIST_LOOKUPS) as PickListField[];
const MULTI_VALUE_FIELDS: PunchlistMultiValueField[] = ['category', 'type'];
/** W74: the punch list each account last saved an item to, per project: { [accountId]: { [projectId]: listId } } */
const LAST_LIST_KEY = 'sitenotes.punch.lastList';

/**
 * The punch list a new item defaults to, among the open ones: opened from a list's Items view (`inViewFirst`), that
 * list, else the one last used here; opened without a list in context (Drawings Add to), the last used first. Else
 * the first open list.
 */
export function defaultPunchList(
  lists: { id?: string; uuid?: string; status?: string }[], remembered: string | null, inView: string | null, inViewFirst = false,
): string | null {
  const open = lists.filter(p => p?.status !== 'closed').map(p => p.uuid || p.id).filter(Boolean);
  const order = inViewFirst ? [inView, remembered] : [remembered, inView];
  return order.find(id => !!id && open.includes(id)) ?? open[0] ?? null;
}

@Component({
  selector: 'app-punchlist-item-form',
  templateUrl: './punchlist-item-form.component.html',
  styleUrls: ['./punchlist-item-form.component.scss'],
  standalone: false,
})
export class PunchlistItemFormComponent implements OnInit, OnDestroy {
  /** A linked issue's status as its badge prints it (a contractor-workflow stage in words). */
  readonly issueStatusText = issueStatusText;

  @Input() data: any;
  @ViewChild('fileInput', { static: false }) fileInputRef!: ElementRef<HTMLInputElement>;

  form: FormGroup;
  saving = false;
  isEdit = false;
  itemId: string | null = null;

  /**
   * The fields this project's items have, in order: its punchlist_item_config
   * when it has one, the built-in fields otherwise. Null until the project is
   * in the store; nothing the config governs is rendered before that, so a
   * field the project hides never shows.
   */
  fields: PunchlistItemField[] | null = null;
  fieldByName: PunchlistItemFieldMap = {};

  /**
   * Pick-list options: the project's lookup values, plus whatever the item
   * being edited holds that the lookup does not have.
   */
  pickListOptions: Record<PickListField, PunchlistValueOption[]> = { category: [], type: [], severity: [] };
  pickListLoading: Record<PickListField, boolean> = { category: false, type: false, severity: false };
  pickListHints: Record<PickListField, string> = { category: '', type: '', severity: '' };
  private lookupValues: Record<PickListField, string[]> = { category: [], type: [], severity: [] };
  /** What the item being edited holds in each pick-list field; empty on a new item. */
  private storedValues: Record<PickListField, string[]> = { category: [], type: [], severity: [] };

  punchlistOptions: { value: string; label: string }[] = [];

  sitesList: { value: any; label: string }[] = [];
  sitesById: Record<string, any> = {};
  locationManuallySet = false;

  latitude: number | null = null;
  longitude: number | null = null;
  /**
   * Design context (spec §12). Like lat/lng, a component field: undefined while untouched on an item that has no
   * link, so a project without design layers sends exactly today's payload (no `map_feature_link` key).
   */
  mapFeatureLink: MapFeatureLink | null | undefined = undefined;
  mediaFiles: any[] = [];

  nearbyIssues: any[] = [];
  nearbyIssuesLoading = false;
  selectedIssueIds: Set<number> = new Set();
  linkedIssues: any[] = [];
  linkedIssuesLoading = false;

  bidItemOptions: any[] = [];
  selectedBidItemIds: any[] = [];

  addMenuOptions = [
    {
      key: 'picture',
      icon: 'paperclip',
      label: 'Attachments',
      handler: () => { this.fileInputRef?.nativeElement?.click(); },
    },
    {
      key: 'location',
      icon: 'marker-outline',
      label: 'Location',
      handler: () => { this.openLocationPicker(); },
    },
  ];

  private readonly onDestroy = new Subject<any>();
  private readonly locationPickerLauncher = inject(LocationPickerLauncherService);
  /** Picker strokes held for a sheet link that exists only once this record saves. */
  private readonly sheetMarkups = inject(SheetMarkupQueueService);
  private readonly auth = inject(AuthService);
  private readonly dialog = inject(MatDialog);

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private appHttpService: AppHttpService,
  ) {
    this.form = this.fb.group({
      punchlist_id: [null, Validators.required],
      title: ['', [Validators.required, Validators.maxLength(500)]],
      description: [''],
      category: [null],
      type: [null],
      severity: [null],
      proposed_solution: [''],
      need_by_date: [null],
      location_description: [''],
      site_id: [null],
      status: ['draft'],
      bidItem: [[]],
    });
  }

  ngOnInit(): void {
    // Build punchlist picker options
    const list = Array.isArray(this.data?.punchlists) && this.data.punchlists.length
      ? this.data.punchlists
      : (this.data?.punchlist ? [this.data.punchlist] : []);
    this.punchlistOptions = list.map((p: any) => ({
      value: p.uuid || p.id,
      label: p.name,
    }));
    // W74: an edit keeps the item's list; a new item takes the last-used open list (defaultPunchList)
    const defaultPunchlistId = this.data?.editItem
      ? this.data.editItem.punchlist_id || this.data?.punchlist?.uuid || this.data?.punchlist?.id || null
      : defaultPunchList(list, this.lastList(), this.data?.punchlist?.uuid || this.data?.punchlist?.id || null, !!this.data?.fromListView);
    this.form.patchValue({ punchlist_id: defaultPunchlistId });

    // A new item opened with a link already chosen (the Drawings viewer's Add to).
    if (!this.data?.editItem && this.data?.map_feature_link) this.mapFeatureLink = this.data.map_feature_link;

    if (this.data?.editItem) {
      this.isEdit = true;
      const c = this.data.editItem;
      this.itemId = c.uuid || c.id;
      this.storedValues = {
        category: punchlistItemValues(c, 'category'),
        type: punchlistItemValues(c, 'type'),
        severity: c.severity ? [c.severity] : [],
      };
      this.form.patchValue({
        title: c.title,
        description: c.description,
        // Single-value shape for now; applyFields() reshapes category and type
        // once the project says whether they take several values.
        category: toPunchlistFormValue(this.storedValues.category, false),
        type: toPunchlistFormValue(this.storedValues.type, false),
        severity: c.severity,
        proposed_solution: c.proposed_solution,
        need_by_date: c.need_by_date,
        location_description: c.location_description,
        site_id: c.site?.id || c.site?.uuid || c.site_id,
        status: c.status,
        bidItem: (c.bid_items || []).map((b: any) => b.id),
      });
      if (c.latitude !== null && c.latitude !== undefined) this.latitude = +c.latitude;
      if (c.longitude !== null && c.longitude !== undefined) this.longitude = +c.longitude;
      if (c.map_feature_link) this.mapFeatureLink = c.map_feature_link;
      const normalize = (item: any, defaultType: 'picture' | 'document') => ({
        ...item,
        fileType: item?.fileType
          || (item?.mime?.startsWith('image/') ? 'picture'
            : item?.mime?.startsWith('video/') ? 'video'
            : defaultType),
      });
      const pics = (c.pictures || []).map((p: any) => normalize(p, 'picture'));
      const docs = (c.attachments || []).map((a: any) => normalize(a, 'document'));
      this.mediaFiles = [...pics, ...docs];
      if (this.mediaFiles.length) {
        setTimeout(() => this.projectsService.mediaData.next(this.mediaFiles));
      }
      // Already-linked issues
      this.fetchLinkedIssues(this.itemId!);
    }

    // The item's own values are options from the start, so an edit shows them
    // while the lookups are still loading (and if they fail to).
    this.refreshPickListOptions();
    PICK_LIST_FIELDS.forEach((field) => this.fetchPickList(field));

    // Field list of the project the form is for. The store keeps whichever
    // project was fetched last and nothing while a fetch is in flight, so only
    // the project in `data.projectId` counts, and the fields it gave stand
    // through a refetch.
    this.store.select(projectDetails)
      .pipe(
        takeUntil(this.onDestroy),
        map(project => projectForRoute(project, this.data?.projectId)),
        filter(project => !!project),
        map(project => resolvePunchlistItemFields(project)),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe((fields: PunchlistItemField[]) => this.applyFields(fields));

    // Load project bid items for the BID ITEM picker
    const projectIdForBidItems = this.data?.projectId;
    if (projectIdForBidItems) {
      this.store.dispatch(FetchAllBidItemsRequest({
        payload: {
          projectId: projectIdForBidItems,
          qp: { include: [{ bid_items: ['fields'] }] },
        },
      }));
    }
    this.store.select(allBidItemsFilter)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((bidItems: any[]) => {
        this.bidItemOptions = bidItems || [];
        // Re-resolve selected display rows once options load.
        const currentSelection = this.form.controls['bidItem']?.value || [];
        this.selectedBidItemIds = (this.bidItemOptions || [])
          .filter((it: any) => currentSelection.includes(it?.id));
      });

    this.form.controls['bidItem']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((ids: any[]) => {
        this.selectedBidItemIds = ids?.length
          ? (this.bidItemOptions || []).filter((it: any) => ids.includes(it?.id))
          : [];
      });

    // Load project sites for the SITE picklist
    this.store.select(projectDetails)
      .pipe(
        takeUntil(this.onDestroy),
        map((project: any) => project?.sites || []),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe((sites: any[]) => {
        this.sitesList = sites.map((s: any) => ({ value: s.id, label: s.name }));
        this.sitesById = sites.reduce((acc: any, s: any) => {
          acc[s.id] = s;
          return acc;
        }, {} as Record<string, any>);
      });

    // When SITE changes, default item lat/lng to the site (unless user manually pinned),
    // and always re-fetch nearby issues so they're filtered by site even if the user
    // has manually pinned a different location.
    this.form.get('site_id')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((siteId: any) => {
        if (!siteId) return;
        const site = this.sitesById[siteId];
        // A linked pin stays put: moving it to the site would leave the link pointing elsewhere.
        if (site && !this.locationManuallySet && !this.mapFeatureLink) {
          const lat = site.latitude ?? site.lat ?? null;
          const lng = site.longitude ?? site.lng ?? site.lon ?? null;
          if (lat !== null && lng !== null) {
            this.latitude = Number(lat);
            this.longitude = Number(lng);
          }
        }
        if (this.latitude !== null && this.longitude !== null) {
          this.fetchNearbyIssues(this.latitude, this.longitude);
        } else {
          // Site has no lat/lng on record — try (0, 0) so backend can still run
          // the site-only filter (radius is dropped when siteId is set).
          this.fetchNearbyIssues(0, 0);
        }
      });

    if (this.latitude !== null && this.longitude !== null) {
      this.fetchNearbyIssues(this.latitude, this.longitude);
    }

    // Listen for uploaded files
    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        const items = JSON.parse(JSON.stringify(Array.isArray(data) ? data : [data]));
        const newFiles = items.filter(
          (item: any) => item?.fileType === 'picture' || item?.fileType === 'document',
        );
        if (newFiles.length) {
          this.mediaFiles = [...this.mediaFiles, ...newFiles];
          this.markDirty();
          setTimeout(() => this.projectsService.mediaData.next(this.mediaFiles));
        }
      });

    // Unsaved changes, as RFIs and issues report them: only the user's own edits (patchValue never sets dirty), so
    // the drawer's close and W70's sheet switch ask before discarding them
    this.projectsService.isAnyFormDirty.next(false);
    this.form.valueChanges
      .pipe(filter(() => this.form.dirty), takeUntil(this.onDestroy))
      .subscribe(() => this.projectsService.isAnyFormDirty.next(true));
  }

  /** An edit outside the form controls (files, location, link, nearby issues) */
  private markDirty(): void {
    this.form.markAsDirty();
    this.projectsService.isAnyFormDirty.next(true);
  }

  /**
   * Category and type hold an array where the project lets them take several
   * values and a single value otherwise. Whatever the control holds is carried
   * over, before the template renders the matching control.
   */
  private applyFields(fields: PunchlistItemField[]): void {
    const fieldByName = punchlistItemFieldMap(fields);
    MULTI_VALUE_FIELDS.forEach((name) => {
      const control = this.form.controls[name];
      const values = fromPunchlistFormValue(control.value, this.storedValues[name]);
      control.setValue(toPunchlistFormValue(values, !!fieldByName[name]?.multiple), { emitEvent: false });
    });
    this.fields = fields;
    this.fieldByName = fieldByName;
    this.refreshPickListOptions();
  }

  private refreshPickListOptions(): void {
    this.pickListOptions = {
      category: punchlistValueOptions(
        this.lookupValues.category, this.storedValues.category, !!this.fieldByName.category?.multiple,
      ),
      type: punchlistValueOptions(
        this.lookupValues.type, this.storedValues.type, !!this.fieldByName.type?.multiple,
      ),
      severity: punchlistValueOptions(this.lookupValues.severity, this.storedValues.severity, false),
    };
  }

  /**
   * Values of one pick list for this project. The API resolves the list (the
   * project's own, else its organization's, else the system defaults) and
   * orders it; the values are used as returned.
   */
  private fetchPickList(field: PickListField): void {
    if (!this.data?.projectId) return;
    this.pickListLoading[field] = true;
    this.appHttpService.getService(
      `/v1/lookup/${PICK_LIST_LOOKUPS[field]}`,
      { entity_type: 'project', entity_id: this.data.projectId },
    ).pipe(takeUntil(this.onDestroy)).subscribe({
      next: (resp) => {
        const rows: { value?: unknown }[] = Array.isArray(resp?.data) ? resp.data : [];
        this.lookupValues[field] = rows
          .map((row) => row?.value)
          .filter((value): value is string => typeof value === 'string' && value !== '');
        this.pickListLoading[field] = false;
        this.refreshPickListOptions();
      },
      error: () => {
        // No list to fall back on: the field keeps the value the item already
        // has (if any) as its only option, and says why there are no others.
        this.pickListLoading[field] = false;
        this.pickListHints[field] = 'Options could not be loaded. Close and reopen the item to try again.';
      },
    });
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    const imageFormData = new FormData();
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (!file) continue;
      if (file.type.startsWith('image/') || file.type.startsWith('application/') || file.type.startsWith('text/')) {
        imageFormData.append('inputFiles', file);
      }
    }
    if (imageFormData.has('inputFiles')) {
      this.store.dispatch(UploadFileRequest({ payload: imageFormData }));
    }
    if (this.fileInputRef) this.fileInputRef.nativeElement.value = '';
  }

  deletePicture(pic: any) {
    this.mediaFiles = this.mediaFiles.filter((p: any) => p.id !== pic.id);
    this.markDirty();
    setTimeout(() => this.projectsService.mediaData.next(this.mediaFiles));
  }

  /**
   * The design-context picker on projects with published layers, else today's Google picker (the launcher decides).
   * Resolves once the picked location is applied.
   */
  async openLocationPicker(): Promise<void> {
    let ref;
    try {
      ref = await this.locationPickerLauncher.open({
        latitude: this.latitude,
        longitude: this.longitude,
        showIncludeGpsLocationCheckbox: false,
        existingLink: this.mapFeatureLink ?? null,
        recordLabel: 'punch item',
      });
    } catch (error) {
      console.error('Location picker failed to open', error);
      return;
    }
    const closed = ref?.afterClosed?.();
    if (!closed) return;
    await new Promise<void>(resolve => closed.pipe(take(1)).subscribe({
      next: resp => { this.applyPickedLocation(resp); resolve(); },
      complete: () => resolve(),
      error: () => resolve(),
    }));
  }

  private applyPickedLocation(resp: unknown): void {
    if (!resp) return;
    const patch = mergePickedLocation(resp);
    if ('map_feature_link' in patch) {
      this.mapFeatureLink = patch.map_feature_link;
      this.markDirty();
    }
    if ('latitude' in patch && 'longitude' in patch) {
      this.latitude = patch.latitude as number;
      this.longitude = patch.longitude as number;
      this.locationManuallySet = true;
      this.markDirty();
      if (this.latitude !== null && this.longitude !== null) {
        this.fetchNearbyIssues(this.latitude, this.longitude);
      }
    }
  }

  /** The card's "Remove link": the pin stays. */
  removeDesignContext(): void {
    this.mapFeatureLink = null;
    this.markDirty();
  }

  /** A snapshot retry / finished render / saved markup: not an unsaved edit. */
  onDesignContextLinkChange(link: MapFeatureLink): void {
    this.mapFeatureLink = link;
  }

  deleteLocation(): void {
    // W77: the link goes with the pin, so markup on it asks first
    const link = this.mapFeatureLink;
    const sheet = link?.sheet;
    const held = !!sheet && !link.uuid && !!this.sheetMarkups.heldFor(link);
    confirmLinkRemoval(this.dialog, link, held).subscribe(go => {
      if (!go) return;
      this.latitude = null;
      this.longitude = null;
      // A link whose pin is gone would keep pointing at the old spot.
      if (this.mapFeatureLink) this.mapFeatureLink = null;
      this.nearbyIssues = [];
      this.selectedIssueIds.clear();
      this.markDirty();
    });
  }

  // ---------------------------------------------------------------------------
  // Nearby issues (Punchlist category only, same site)
  // ---------------------------------------------------------------------------

  fetchNearbyIssues(lat: number, lng: number) {
    if (!this.data?.projectId) return;
    this.nearbyIssuesLoading = true;
    const siteId = this.form.value.site_id || undefined;
    this.appHttpService.getService(
      `/v1/punchlist/project/${this.data.projectId}/nearby-issues`,
      {
        lat, lng, radius: 200, type: 'punchlist', item_id: this.itemId || undefined, site_id: siteId,
      },
    ).subscribe({
      next: (resp: any) => {
        this.nearbyIssues = resp?.data || resp || [];
        this.nearbyIssuesLoading = false;
      },
      error: () => {
        this.nearbyIssues = [];
        this.nearbyIssuesLoading = false;
      },
    });
  }

  fetchLinkedIssues(itemId: string) {
    this.linkedIssuesLoading = true;
    this.appHttpService.getService(`/v1/punchlist/items/${itemId}/issues`).subscribe({
      next: (resp: any) => {
        this.linkedIssues = resp?.data || resp || [];
        this.linkedIssuesLoading = false;
      },
      error: () => {
        this.linkedIssues = [];
        this.linkedIssuesLoading = false;
      },
    });
  }

  toggleIssueSelection(issueId: number) {
    if (this.selectedIssueIds.has(issueId)) this.selectedIssueIds.delete(issueId);
    else this.selectedIssueIds.add(issueId);
    this.markDirty();
  }

  isIssueSelected(issueId: number): boolean { return this.selectedIssueIds.has(issueId); }

  isIssueLinked(issueId: number): boolean {
    return this.linkedIssues.some((li) => li.id === issueId);
  }

  unlinkIssue(issue: any) {
    if (!this.itemId) return;
    this.appHttpService.deleteService(`/v1/punchlist/items/${this.itemId}/issues/${issue.id}`).subscribe({
      next: () => {
        this.linkedIssues = this.linkedIssues.filter((li) => li.id !== issue.id);
        const match = this.nearbyIssues.find((ni) => ni.id === issue.id);
        if (match) match.linked_to_item = false;
      },
    });
  }

  formatDistance(meters: number): string {
    if (meters == null) return '';
    if (meters < 1000) return `${Math.round(meters)}m away`;
    return `${(meters / 1000).toFixed(1)}km away`;
  }

  private linkIssuesToItem(itemId: string) {
    const issueIds = Array.from(this.selectedIssueIds);
    return this.appHttpService.postService(
      `/v1/punchlist/items/${itemId}/issues`,
      { issue_ids: issueIds },
    );
  }

  save(submit = false): void {
    // The api refuses edits once the item leaves draft; don't send one from a stale panel.
    if (this.isEdit && !canModifyPunchlistItem(this.data?.editItem)) {
      this.commonService.errorNotification('Only draft punch list items can be edited');
      return;
    }
    // Without the project's field list there is no telling which fields are
    // hidden and must stay out of the payload.
    if (!this.fields) return;
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    const pictureIds = this.mediaFiles.filter((m: any) => m.fileType === 'picture').map((m: any) => m.id);
    const attachmentIds = this.mediaFiles.filter((m: any) => m.fileType === 'document').map((m: any) => m.id);
    const {
      bidItem, category, type, ...formValue
    } = this.form.value;
    // Category and type travel as arrays (`categories` / `types`); the API
    // derives the single strings, which are not sent. A field the project
    // hides is left out altogether: the API leaves an absent field untouched
    // on update and clears one that is sent empty.
    const payload: any = withoutHiddenPunchlistItemFields({
      ...formValue,
      categories: fromPunchlistFormValue(category, this.storedValues.category),
      types: fromPunchlistFormValue(type, this.storedValues.type),
      latitude: this.latitude,
      longitude: this.longitude,
      pictures: pictureIds,
      attachments: attachmentIds,
      bid_item_ids: Array.isArray(bidItem) ? bidItem.filter(Boolean) : [],
    }, this.fields);
    // The design context is map data, not a config field, so it goes on after the hidden-field filter.
    // Tri-state: omitted while untouched (no layers → today's payload), null clears, else the api input shape.
    // undefined until a link is loaded or picked, so null is always a removal made here: { sheet: null } (R54)
    const link = recordLinkPayload(this.mapFeatureLink, this.latitude, this.longitude, this.mapFeatureLink === null);
    if (link !== undefined) payload.map_feature_link = link;
    if (submit) payload.status = 'submitted';

    const selectedPunchlistId = this.form.value.punchlist_id
      || this.data.punchlist?.uuid
      || this.data.punchlist?.id;
    const op$ = this.isEdit
      ? this.projectsService.updatePunchlistItem({ itemId: this.itemId, data: payload })
      : this.projectsService.createPunchlistItem({
        projectId: this.data.projectId,
        punchlistId: selectedPunchlistId,
        data: payload,
      });

    op$.pipe(takeUntil(this.onDestroy)).subscribe({
      next: (resp: any) => {
        void this.sheetMarkups.flush(resp);
        this.rememberList(this.form.value.punchlist_id);
        const savedItem = resp?.data?.attributes
          ? { ...resp.data, ...resp.data.attributes }
          : resp?.data || {};
        const savedItemId = savedItem.uuid || savedItem.id || this.itemId;
        // Link selected nearby issues
        if (savedItemId && this.selectedIssueIds.size > 0) {
          this.linkIssuesToItem(savedItemId).subscribe({
            next: () => this.finishSave(),
            error: () => this.finishSave(),
          });
        } else {
          this.finishSave();
        }
      },
      error: (err: any) => {
        this.saving = false;
        this.commonService.errorNotification(err?.error?.error || 'Failed to save item');
      },
    });
  }

  /** W74: the list this account last saved an item to on this project (localStorage; private mode may refuse it) */
  private lastList(): string | null {
    const account = this.auth.getCurrentUser()?.id;
    try {
      const all = JSON.parse(localStorage.getItem(LAST_LIST_KEY) ?? '{}');
      return (account && this.data?.projectId && all?.[account]?.[this.data.projectId]) || null;
    } catch {
      return null;
    }
  }

  private rememberList(listId: string | null): void {
    const account = this.auth.getCurrentUser()?.id;
    if (!account || !listId || !this.data?.projectId) {
      return;
    }
    try {
      const all = JSON.parse(localStorage.getItem(LAST_LIST_KEY) ?? '{}') || {};
      all[account] = { ...(all[account] ?? {}), [this.data.projectId]: listId };
      localStorage.setItem(LAST_LIST_KEY, JSON.stringify(all));
    } catch {
      // Storage full or blocked: the default falls back to the first open list
    }
  }

  private finishSave() {
    this.saving = false;
    this.projectsService.isAnyFormDirty.next(false);
    this.commonService.notification(this.isEdit ? 'Item updated' : 'Item created', 'success');
    this.projectsService.punchlistChanged.next();
    this.projectsService.closeRightPanel.next();
  }

  ngOnDestroy(): void {
    // W74 review I1: closed any other way (a tab switch, another record in the drawer) the edits are gone, so is the flag
    this.projectsService.isAnyFormDirty.next(false);
    this.onDestroy.next(null); this.onDestroy.complete();
  }
}
